import { useMemo } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Tooltip } from '@/components/General/Tooltip';
import { computePathProgress } from '@/utils/followUpPaths';

/**
 * PathProgressBar — the single segmented progress bar for a flat Path
 * enrollment (§25.8):
 *
 *   [ green: successful ][ primary: progressed-unsuccessful ][ muted: rest ]
 *
 * One shared denominator (effective = non-RETIRED steps) drives both
 * percentages so a partial Path can never read as "100% successful":
 *   pathProgress    = progressed / effective  (SUCCEEDED+EXPIRED+SKIPPED)
 *   stepsSuccessful = successful / effective  (SUCCEEDED only)
 * Cancelled work from an early stop stays muted — a stop never
 * manufactures 100% progress. Percentages are visible without the tooltip;
 * counts ride in the accessible label and the hover/tap detail.
 */
const PathProgressBar = ({ progress, testID = 'path-progress' }) => {
    const computed = useMemo(() => computePathProgress(progress), [progress]);

    const detailParts = [
        `${computed.successful} successful`,
        computed.expired > 0 ? `${computed.expired} expired` : null,
        computed.skipped > 0 ? `${computed.skipped} skipped` : null,
    ].filter(Boolean);
    const detail = detailParts.join(' · ') || 'No resolved steps';
    const summaryText = `${computed.pathProgressPct}% path progress · ${computed.stepsSuccessfulPct}% steps successful`;

    return (
        <View style={styles.wrap} testID={testID}>
            <Tooltip
                title={`${summaryText}. ${detail} of ${computed.effective} step${computed.effective === 1 ? '' : 's'}.`}
                appearOnTouch
            >
                <View
                    style={styles.track}
                    accessibilityRole="progressbar"
                    accessibilityLabel={`${summaryText}. ${detail}.`}
                    accessibilityValue={{
                        min: 0,
                        max: computed.effective,
                        now: computed.progressed,
                        text: summaryText,
                    }}
                >
                    {computed.successfulWidth > 0 && (
                        <View style={[styles.segment, styles.segmentSuccess, { width: `${computed.successfulWidth}%` }]} />
                    )}
                    {computed.missedWidth > 0 && (
                        <View style={[styles.segment, styles.segmentMissed, { width: `${computed.missedWidth}%` }]} />
                    )}
                </View>
            </Tooltip>
            <View style={styles.labelsRow}>
                <Text style={styles.summary} testID={`${testID}-summary`}>{summaryText}</Text>
                <Text style={styles.detail} testID={`${testID}-detail`}>{detail}</Text>
            </View>
            <View style={styles.legendRow}>
                <View style={styles.legendItem}>
                    <View style={[styles.legendDot, styles.legendSuccess]} />
                    <Text style={styles.legendText}>Successful</Text>
                </View>
                <View style={styles.legendItem}>
                    <View style={[styles.legendDot, styles.legendMissed]} />
                    <Text style={styles.legendText}>Progressed, not successful</Text>
                </View>
                <View style={styles.legendItem}>
                    <View style={[styles.legendDot, styles.legendMuted]} />
                    <Text style={styles.legendText}>Remaining</Text>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    wrap: { gap: 6 },
    track: {
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.borderSubtle,
        flexDirection: 'row',
        overflow: 'hidden',
    },
    segment: { height: '100%' },
    segmentSuccess: { backgroundColor: colors.success },
    segmentMissed: { backgroundColor: colors.primary },
    labelsRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 8,
        flexWrap: 'wrap',
    },
    summary: { fontSize: 12, fontFamily: fonts.bold, color: colors.text },
    detail: { fontSize: 12, color: colors.textSecondary },
    legendRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    legendItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    legendDot: { width: 8, height: 8, borderRadius: 4 },
    legendSuccess: { backgroundColor: colors.success },
    legendMissed: { backgroundColor: colors.primary },
    legendMuted: { backgroundColor: colors.borderSubtle },
    legendText: { fontSize: 11, color: colors.textTertiary },
}));

export default PathProgressBar;
